import React, { useState, useEffect, useMemo } from "react";
import {
  Box, Button, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, IconButton, TextField, InputAdornment, MenuItem,
  Select, Chip, Pagination, Dialog, DialogTitle, DialogContent, Menu, DialogActions,
  Card, CardContent, Divider, useTheme, TableSortLabel, Grid
} from "@mui/material";
import { Helmet } from "react-helmet";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import Avatar from "@mui/material/Avatar";
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Toast from "../components/Toast";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import BusinessIcon from '@mui/icons-material/Business';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AndroidIcon from '@mui/icons-material/Android';
import AppleIcon from '@mui/icons-material/Apple';
import CloseIcon from '@mui/icons-material/Close';
import { useUser } from "../context/UserContext";
import Tooltip from '@mui/material/Tooltip';
import SettingsIcon from '@mui/icons-material/Settings';
import ModuleSettingsModal from "../components/ModuleSettingsModal";
import ArchiveManager from "../utils/archiveManager";
import DraftManager from "../utils/draftManager";
import DraftsModal from "../components/DraftsModal";
import HelpTooltip from "../components/HelpTooltip";
import ArchiveIcon from "@mui/icons-material/Archive";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DraftsIcon from "@mui/icons-material/Drafts";
import InventoryIcon from "@mui/icons-material/Inventory";


const statusColors = {
  active: "success",
  inactive: "warning",
  pending: "error"
};


function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function daysAgo(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "today";
  if (diff === 1) return "1 day ago";
  return `${diff} days ago`;
}

const Organizations = () => {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All Status");
  const [selected, setSelected] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [page, setPage] = useState(1);
  const [totalOrganizations, setTotalOrganizations] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [selectedOrgForView, setSelectedOrgForView] = useState(null);
  const [orgDetailModalOpen, setOrgDetailModalOpen] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [Bulkstatus, setBulkstatus] = useState(1); // Default status for adding to My Songs
  const { user, setUser } = useUser();
  const API_URL = process.env.REACT_APP_API_URL || 'http://45.194.3.128:4000/api/admin/';
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState('');
  const [switchingOrgId, setSwitchingOrgId] = useState(null);

  // Drafts state
  const [draftsOpen, setDraftsOpen] = useState(false);
  const [draftsList, setDraftsList] = useState(() => DraftManager.getDrafts('organizations'));
  const [activeDraftId, setActiveDraftId] = useState(null);

  // Archive state
  const [archivedList, setArchivedList] = useState(() => ArchiveManager.getArchivedItems('organizations'));

  const handleArchiveOrg = (org) => {
    ArchiveManager.archiveItem('organizations', org);
    setArchivedList(ArchiveManager.getArchivedItems('organizations'));
    setToast({ open: true, message: `"${org.org_name}" archived and hidden from mobile app`, severity: 'info' });
  };

  const handleRestoreOrg = (orgId) => {
    ArchiveManager.restoreItem('organizations', orgId);
    setArchivedList(ArchiveManager.getArchivedItems('organizations'));
    setToast({ open: true, message: "Organization restored to active list and mobile app", severity: 'success' });
  };

  const handleDeleteOrgPermanently = (orgId) => {
    ArchiveManager.deletePermanently('organizations', orgId);
    setArchivedList(ArchiveManager.getArchivedItems('organizations'));
    setToast({ open: true, message: "Archived organization permanently deleted", severity: 'success' });
  };

  const handleSaveOrgDraft = (showToast = true) => {
    if (!editOrg) return;
    const currentDrafts = DraftManager.getDrafts('organizations');
    if (!activeDraftId && currentDrafts.length >= 3) {
      if (showToast) setToast({ open: true, message: "Maximum 3 drafts allowed. Please delete or resume an existing draft first.", severity: "warning" });
      return;
    }
    const title = editOrg.org_name ? `Org: ${editOrg.org_name}` : `Org ID: ${editOrg.id}`;
    const saved = DraftManager.saveDraft('organizations', editOrg, title, activeDraftId);
    if (saved) {
      setActiveDraftId(saved.id);
      setDraftsList(DraftManager.getDrafts('organizations'));
      if (showToast) setToast({ open: true, message: "Draft saved successfully (Max 3)", severity: "success" });
    }
  };

  const handleResumeOrgDraft = (draft) => {
    if (!draft || !draft.data) return;
    setActiveDraftId(draft.id);
    setEditOrg(draft.data);
    setDraftsOpen(false);
    setToast({ open: true, message: "Draft loaded into editor", severity: "info" });
  };

  const handleDeleteOrgDraft = (draftId) => {
    DraftManager.deleteDraft('organizations', draftId);
    setDraftsList(DraftManager.getDrafts('organizations'));
    if (activeDraftId === draftId) setActiveDraftId(null);
    setToast({ open: true, message: "Draft deleted", severity: "info" });
  };

  const handleSwitchToOrganization = async (targetOrgId, orgName) => {
    try {
      setSwitchingOrgId(targetOrgId);
      setToast({ open: true, message: `Switching context to "${orgName}"...`, severity: 'info' });
      const currentToken = localStorage.getItem('token');
      const currentUserInfo = localStorage.getItem('userInfo');

      // Backup SA session if not already backed up
      if (!sessionStorage.getItem('gpn_sa_token') && !localStorage.getItem('gpn_sa_token')) {
        sessionStorage.setItem('gpn_sa_token', currentToken);
        sessionStorage.setItem('gpn_sa_userInfo', currentUserInfo);
        localStorage.setItem('gpn_sa_token', currentToken);
        localStorage.setItem('gpn_sa_userInfo', currentUserInfo);
      }

      const res = await fetch(`${API_URL}switch-organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentToken}`
        },
        body: JSON.stringify({ org_id: targetOrgId })
      });
      const data = await res.json();
      if (data.success) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('userInfo', JSON.stringify(data.user));
        if (setUser) setUser(data.user);

        const orgModeData = {
          orgId: targetOrgId,
          orgName: data.user?.organization || orgName,
          orgLogo: data.user?.organization_logo || null,
        };
        sessionStorage.setItem('gpn_org_view_mode', JSON.stringify(orgModeData));
        localStorage.setItem('gpn_org_view_mode', JSON.stringify(orgModeData));

        setToast({ open: true, message: `Successfully switched to ${orgName}! Redirecting to dashboard...`, severity: 'success' });
        setTimeout(() => {
          window.location.href = '/admin/dashboard';
        }, 500);
      } else {
        setToast({ open: true, message: data.message || 'Failed to switch organization', severity: 'error' });
      }
    } catch (err) {
      console.error('Error switching organization:', err);
      setToast({ open: true, message: 'Failed to switch organization', severity: 'error' });
    } finally {
      setSwitchingOrgId(null);
    }
  };

    const handleCopyLink = (link, linkName) => {
    navigator.clipboard.writeText(link).then(() => {
      setCopiedLink(linkName);
      setToast({ open: true, message: `${linkName} copied to clipboard!`, severity: 'success' });
      setTimeout(() => setCopiedLink(''), 2000);
    }).catch(err => {
      setToast({ open: true, message: 'Failed to copy link', severity: 'error' });
    });
  };

  const handleEditOrganization = (org) => {
    console.log(org)
    setEditOrg(org);
  };

  const selectedUsers = organizations
    .filter((organization) => selected.includes(organization.id))
    .map((organization) => ({
      id: organization.id,
      name: organization.org_name || "Unknown Organization",
    }));

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(organizations.map((org) => org.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const BulkUpdateToUser = async () => {
    if (selected.length === 0) {
      setToast({ open: true, message: "Please select at least one user.", severity: "warning" });
      return;
    }
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}bulk-update-organizations`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        organizations: selected,
        status: Bulkstatus,
      }),
    });
    const data = await response.json();

    if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }

          setToast({ open: true, message: data?.message || 'Failed to update organizations', severity: "error" });

    } else {

      setToast({ open: true, message: Bulkstatus === 1 ? "Organizations activated successfully" : "Organizations deactivated successfully", severity: "success" });
      setOpenViewDialog(false);
      setSelected([]);
      //  setselectedUsers([]);
      setTimeout(() => {
        window.location.reload();
      }, 1500);

  }
  };

  useEffect(() => {
    const fetchOrganizations = async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}organizations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          search: search,
          status: status,
          page: page,
          pageSize: pageSize
        })
      });
      const data = await response.json();
      setOrganizations(data.organisations);
      setTotalOrganizations(data.totalCount);
      setTotalPages(data.totalPages);
    };
    fetchOrganizations();
  }, [page, pageSize, search, status]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };


  const handleStatusChange = async (id, status) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}update-organization-status`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status: status, id: id })
    });
    const data = await response.json();

     if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }
    }

    setToast({ open: true, message: "Status updated successfully", severity: "success" });
      setTimeout(() => {
        window.location.reload();
      }, 1500);

   
  };

  const updateEditStatus = async (id, status) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}editorganizationstatus`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status: status, id: id })
    });
    const data = await response.json();

     if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }
    }
      setToast({ open: true, message: "Status updated successfully", severity: "success" });
      setTimeout(() => {
        window.location.reload();
      }, 1500);

   
  };



  const handleEdit = (id) => {
    console.log(id);
  };
  const saveEdit = async () => {
    const formData = new FormData();
    formData.append("org_name", editOrg.org_name);
    formData.append("contact_person_name", editOrg.contact_person_name);
    formData.append("email", editOrg.email);
    formData.append("phone", editOrg.phone);
    formData.append("address", editOrg.address);
    formData.append("website", editOrg.website);

    // Only append logo if it's a File (not a string URL)
    if (editOrg.logo && editOrg.logo instanceof File) {
      formData.append("logo", editOrg.logo);
    }

    formData.append("id", editOrg.id);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}sp-update-organization`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
          // ❌ Do NOT set 'Content-Type' when sending FormData
        },
        body: formData
      });

      const data = await response.json();

       if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }

          setToast({ open: true, message: "Update failed: " + (data?.message || 'Unknown error'), severity: "error" });
      }
        setToast({ open: true, message: "Organization updated successfully", severity: "success" });
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      
    } catch (error) {
      console.error("Error while updating:", error);
      setToast({ open: true, message: "Something went wrong: " + error.message, severity: "error" });
    }
  };



  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleSelectOption = (status) => {
    setOpenViewDialog(true);
    setBulkstatus(status);
  }


  const handleDialogClose = () => {
    setOpenViewDialog(false);
  };

  const [sortOrder, setSortOrder] = useState('asc');
  const isArchived = status === 'archived';
  const activeOrgs = organizations.filter(o => !archivedList.some(a => String(a.id) === String(o.id)));
  const baseOrgs = isArchived ? archivedList : activeOrgs;
  const displayedOrganizations = useMemo(() => {
    return [...baseOrgs].sort((a, b) => {
      const nameA = (a.org_name || a.name || '').trim().toLowerCase();
      const nameB = (b.org_name || b.name || '').trim().toLowerCase();
      if (sortOrder === 'desc') return nameB.localeCompare(nameA);
      return nameA.localeCompare(nameB);
    });
  }, [baseOrgs, sortOrder]);

  return (
    <>
      <Helmet>
        <title>Organizations - GPN</title>
      </Helmet>
      <Box>
        {/* Compact Header */}
        <Box sx={{
          mb: 1.5,
          py: 1.25,
          px: 2,
          background: isLight
            ? '#FFFFFF'
            : 'linear-gradient(135deg, rgba(76, 161, 175, 0.15) 0%, rgba(44, 62, 80, 0.15) 100%)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(76, 161, 175, 0.3)',
          boxShadow: isLight ? '0 1px 3px rgba(15,23,42,0.05)' : '0 4px 12px rgba(76, 161, 175, 0.2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, color: isLight ? '#0F172A' : '#FFFFFF', letterSpacing: '-0.3px', lineHeight: 1.2 }}>
              Organization Management
            </Typography>
            <Typography variant="caption" sx={{ color: isLight ? '#64748B' : 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}>
              Manage all partner ministries and organizations with instant alphabetical filtering.
            </Typography>
          </Box>
        </Box>

        {/* Compact 1-Row Toolbar */}
        <Paper sx={{
          p: 1.5,
          mb: 2,
          borderRadius: 2,
          background: isLight ? '#FFFFFF' : 'rgba(255,255,255,0.05)',
          backdropFilter: isLight ? 'none' : 'blur(20px)',
          border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.1)',
          boxShadow: isLight ? '0 1px 3px rgba(15,23,42,0.04)' : undefined,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
        }}>
          {/* Left: Heading & Count */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: isLight ? '#0F172A' : '#FFFFFF', fontSize: '0.95rem' }}>
              All Organizations
            </Typography>
            <Chip
              size="small"
              label={`${displayedOrganizations.length} Total`}
              sx={{
                bgcolor: isLight ? '#E0F2FE' : 'rgba(76, 161, 175, 0.2)',
                color: isLight ? '#0284C7' : '#64d2e2',
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 22
              }}
            />
          </Box>

          {/* Right: Search, Status, Sort, Drafts */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search organizations..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: isLight ? '#64748B' : 'rgba(255,255,255,0.6)' }} />
                  </InputAdornment>
                )
              }}
              sx={{ width: { xs: '100%', sm: 220 }, '& .MuiInputBase-input': { py: 0.6, fontSize: '0.85rem' } }}
            />
            <Select
              size="small"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              sx={{ width: { xs: '100%', sm: 145 }, '& .MuiSelect-select': { py: 0.6, fontSize: '0.85rem' } }}
            >
              <MenuItem value="All Status">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="archived">Archived ({archivedList.length})</MenuItem>
            </Select>

            <Select
              size="small"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              displayEmpty
              renderValue={(val) => `Sort: ${val === 'asc' ? 'A → Z' : 'Z → A'}`}
              sx={{ width: { xs: '100%', sm: 135 }, '& .MuiSelect-select': { py: 0.6, fontSize: '0.85rem' } }}
            >
              <MenuItem value="asc">Name: A → Z (Alphabetical)</MenuItem>
              <MenuItem value="desc">Name: Z → A</MenuItem>
            </Select>

            <Button
              variant="outlined"
              size="small"
              startIcon={<DraftsIcon sx={{ fontSize: 18 }} />}
              onClick={() => setDraftsOpen(true)}
              sx={{
                py: 0.5,
                px: 1.2,
                fontSize: '0.82rem',
                color: draftsList.length > 0 ? (isLight ? "#0D9488" : "#64d2e2") : (isLight ? "#64748B" : "rgba(255,255,255,0.7)"),
                borderColor: draftsList.length > 0 ? (isLight ? "#0D9488" : "#4CA1AF") : (isLight ? "#CBD5E1" : "rgba(255,255,255,0.3)"),
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 1.5,
                "&:hover": { borderColor: isLight ? "#0D9488" : "#64d2e2", backgroundColor: isLight ? "rgba(13, 148, 136, 0.08)" : "rgba(76, 161, 175, 0.1)" },
              }}
            >
              Drafts ({draftsList.length}/3)
            </Button>
          </Box>
        </Paper>



        {/* Table */}
        <Paper sx={isLight ? { background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 3, boxShadow: '0 1px 3px rgba(15,23,42,0.05)', overflow: 'hidden' } : {}}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">

                    <Box display="flex" alignItems="center">
                      <Checkbox
                        checked={selected.length === organizations.length}
                        indeterminate={selected.length > 0 && selected.length < organizations.length}
                        onChange={handleSelectAll}
                      />
                      <IconButton size="small" onClick={handleMenuOpen}>
                        <ArrowDropDownIcon />
                      </IconButton>

                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleMenuClose}
                      >

                        <MenuItem onClick={() => { handleSelectOption(1); handleMenuClose(); }}>
                          Activate
                        </MenuItem>
                        <MenuItem onClick={() => { handleSelectOption(2); handleMenuClose(); }}>
                          Deactivate
                        </MenuItem>

                      </Menu>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <TableSortLabel
                      active={true}
                      direction={sortOrder}
                      onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                    >
                      Organization
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Members</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>

                {displayedOrganizations.length > 0 ? (
                  displayedOrganizations.map((org) => (
                    <TableRow
                      key={org.id}
                      hover
                      onClick={() => {
                        setSelectedOrgForView(org);
                        setOrgDetailModalOpen(true);
                      }}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: isLight ? 'rgba(13, 148, 136, 0.08) !important' : 'rgba(76, 161, 175, 0.12) !important' }
                      }}
                    >
                      <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selected.includes(org.id)}
                          onChange={() => handleSelect(org.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSwitchToOrganization(org.id, org.org_name);
                          }}
                          title="Click to switch context into this organization"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            cursor: "pointer",
                            p: 0.8,
                            borderRadius: 2,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              background: isLight ? "rgba(13, 148, 136, 0.08)" : "rgba(76, 161, 175, 0.15)",
                              transform: "translateX(2px)",
                              "& .org-title-hover": {
                                color: isLight ? "#0D9488" : "#4CA1AF",
                                textDecoration: "underline",
                              },
                            },
                          }}
                        >
                          <Avatar
                            src={org.logo || undefined}
                            alt={org.org_name}
                            sx={{
                              width: 42,
                              height: 42,
                              borderRadius: 2,
                              background: isLight ? "#F0FDFA" : "linear-gradient(135deg, #203A43 0%, #2C5364 100%)",
                              color: isLight ? "#0D9488" : "#4CA1AF",
                              fontWeight: 700,
                              fontSize: 18,
                              border: isLight ? "1px solid #CCFBF1" : "1px solid rgba(76, 161, 175, 0.3)",
                              boxShadow: isLight ? "0 1px 2px rgba(15,23,42,0.05)" : "0 2px 8px rgba(0,0,0,0.2)",
                            }}
                            imgProps={{
                              onError: (e) => {
                                e.target.onerror = null;
                                e.target.src = "";
                              },
                            }}
                          >
                            {org.org_name ? org.org_name[0].toUpperCase() : <BusinessIcon sx={{ fontSize: 22 }} />}
                          </Avatar>
                          <Box>
                            <Typography
                              className="org-title-hover"
                              fontWeight={600}
                              sx={{
                                color: isLight ? "#0F172A" : "#FFFFFF",
                                transition: "color 0.2s ease",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.8,
                              }}
                            >
                              {org.org_name}
                              <SwapHorizIcon sx={{ fontSize: 16, opacity: 0.7, color: isLight ? "#0D9488" : "#4CA1AF" }} />
                              {org.update_request === "Requested" && (
                                <Chip
                                  label="Update Request"
                                  size="small"
                                  variant="outlined"
                                  color="warning"
                                  sx={{ height: 20, fontSize: "0.65rem" }}
                                />
                              )}
                            </Typography>
                            <Typography variant="caption" sx={{ color: isLight ? "#94A3B8" : "rgba(255, 255, 255, 0.5)" }}>
                              Click to switch context
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ color: isLight ? "#0F172A" : undefined, fontWeight: 500 }}>{org.contact}</Typography>
                        <Typography variant="body2" sx={{ color: isLight ? "#64748B" : "text.secondary" }}>{org.email}</Typography>
                        <Typography variant="body2" sx={{ color: isLight ? "#64748B" : "text.secondary" }}>{org.phone}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={org.status}
                          color={statusColors[org.status]}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ color: isLight ? "#0F172A" : undefined }}>{formatDate(org.created_at)}</Typography>
                        <Typography variant="body2" sx={{ color: isLight ? "#64748B" : "text.secondary" }}>{daysAgo(org.created_at)}</Typography>
                      </TableCell>
                      <TableCell sx={{ color: isLight ? "#0F172A" : undefined, fontWeight: 600 }}>{org.total_users}</TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton title="Edit" onClick={() => handleEditOrganization(org)} sx={{ color: isLight ? '#0D9488' : '#4CA1AF' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>

                        {isArchived ? (
                          <>
                            <IconButton
                              title="Restore to Active"
                              onClick={() => handleRestoreOrg(org.id)}
                              sx={{ color: '#10B981' }}
                            >
                              <UnarchiveIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              title="Delete Permanently"
                              onClick={() => handleDeleteOrgPermanently(org.id)}
                              sx={{ color: '#DC2626' }}
                            >
                              <DeleteForeverIcon fontSize="small" />
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton
                              title="Archive (Hide from App)"
                              onClick={() => handleArchiveOrg(org)}
                              sx={{ color: '#F59E0B' }}
                            >
                              <ArchiveIcon fontSize="small" />
                            </IconButton>

                            {org.status === "pending" && (
                              <>
                                <IconButton
                                  title="Activate"
                                  onClick={() => handleStatusChange(org.id, "active")}
                                >
                                  <CheckCircleIcon color="success" fontSize="small" />
                                </IconButton>
                                <IconButton
                                  title="Inactivate"
                                  onClick={() => handleStatusChange(org.id, "inactive")}
                                >
                                  <CancelIcon color="error" fontSize="small" />
                                </IconButton>
                              </>
                            )}

                            {org.status === "active" && (
                              <IconButton
                                title="Inactivate"
                                onClick={() => handleStatusChange(org.id, "inactive")}
                              >
                                <CancelIcon color="error" fontSize="small" />
                              </IconButton>
                            )}

                            {org.status === "inactive" && (
                              <IconButton
                                title="Activate"
                                onClick={() => handleStatusChange(org.id, "active")}
                              >
                                <CheckCircleIcon color="success" fontSize="small" />
                              </IconButton>
                            )}
                          </>
                        )}
                      </TableCell>

                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center", color: isLight ? '#94A3B8' : undefined }}>
                      No Organizations found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1.5 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              {displayedOrganizations.length > 0 ? (
                displayedOrganizations.map((org) => (
                  <Card
                    key={org.id}
                    variant="outlined"
                    onClick={() => {
                      setSelectedOrgForView(org);
                      setOrgDetailModalOpen(true);
                    }}
                    sx={{ 
                      borderRadius: 2, 
                      cursor: 'pointer',
                      background: isLight ? '#FFFFFF' : 'rgba(255,255,255,0.03)',
                      borderColor: isLight ? '#E2E8F0' : 'rgba(255,255,255,0.1)',
                      boxShadow: isLight ? '0 1px 3px rgba(15,23,42,0.05)' : undefined,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderColor: isLight ? '#0D9488' : '#4CA1AF',
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSwitchToOrganization(org.id, org.org_name);
                        }}
                        sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, cursor: 'pointer' }}
                      >
                        <Avatar
                          src={org.logo || undefined}
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            background: isLight ? "#F0FDFA" : "linear-gradient(135deg, #203A43 0%, #2C5364 100%)",
                            color: isLight ? "#0D9488" : "#4CA1AF",
                            fontWeight: 700,
                            fontSize: 20,
                            border: isLight ? "1px solid #CCFBF1" : "1px solid rgba(76, 161, 175, 0.3)",
                          }}
                          imgProps={{
                            onError: (e) => { e.target.onerror = null; e.target.src = ""; }
                          }}
                        >
                          {org.org_name ? org.org_name[0].toUpperCase() : <BusinessIcon />}
                        </Avatar>
                        <Box>
                          <Typography fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF", display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {org.org_name}
                            <SwapHorizIcon sx={{ fontSize: 16, color: isLight ? '#0D9488' : '#4CA1AF' }} />
                          </Typography>
                          <Typography variant="caption" sx={{ color: isLight ? "#94A3B8" : "rgba(255,255,255,0.6)" }}>{org.contact || "Click to switch"}</Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Box>
                          <Typography variant="caption" sx={{ color: isLight ? "#94A3B8" : "rgba(255,255,255,0.5)" }} display="block">Status</Typography>
                          <Chip 
                            label={org.status} 
                            color={statusColors[org.status]} 
                            size="small" 
                            sx={{ height: 20, fontSize: '0.65rem' }} 
                          />
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ color: isLight ? "#94A3B8" : "rgba(255,255,255,0.5)" }} display="block">Users</Typography>
                          <Typography variant="body2" sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }} fontWeight={600}>{org.total_users}</Typography>
                        </Box>
                      </Box>

                      <Divider sx={{ my: 1.5, borderColor: isLight ? '#F1F5F9' : 'rgba(255,255,255,0.05)' }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ color: isLight ? "#94A3B8" : "rgba(255,255,255,0.4)" }}>
                          Joined: {formatDate(org.created_at)}
                        </Typography>
                        <Box onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" sx={{ color: isLight ? '#0D9488' : '#4CA1AF' }} onClick={() => handleEditOrganization(org)} title="Edit">
                            <EditIcon fontSize="small" />
                          </IconButton>
                          {isArchived ? (
                            <>
                              <IconButton size="small" sx={{ color: '#10B981' }} onClick={() => handleRestoreOrg(org.id)} title="Restore to Mobile App">
                                <UnarchiveIcon fontSize="small" />
                              </IconButton>
                              <IconButton size="small" sx={{ color: '#DC2626' }} onClick={() => handleDeleteOrgPermanently(org.id)} title="Delete Forever">
                                <DeleteForeverIcon fontSize="small" />
                              </IconButton>
                            </>
                          ) : (
                            <>
                              <IconButton size="small" sx={{ color: '#F59E0B' }} onClick={() => handleArchiveOrg(org)} title="Archive (Hide from App)">
                                <ArchiveIcon fontSize="small" />
                              </IconButton>
                              {org.status === 'pending' && (
                                <IconButton size="small" sx={{ color: '#10B981' }} onClick={() => handleStatusChange(org.id, "active")} title="Activate">
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                              )}
                            </>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Typography align="center" sx={{ py: 4, color: isLight ? '#94A3B8' : 'rgba(255,255,255,0.5)' }}>
                  {isArchived ? "No archived organizations found" : "No Organizations found"}
                </Typography>
              )}
            </Box>
          </Box>
          {/* Pagination */}

          {totalOrganizations > 0 && (
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
              <Typography variant="body2">
                Showing 1-10 of {totalOrganizations} organizations
              </Typography>
              <Pagination count={totalPages} page={page} siblingCount={1} boundaryCount={1} onChange={handlePageChange} />
            </Box>

          )}
        </Paper>


        {/* Edit Organization Dialog */}

        {editOrg && (
          <Dialog
            open={!!editOrg}
            onClose={() => setEditOrg(null)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                background: isLight ? '#FFFFFF' : undefined,
                border: isLight ? '1px solid #E2E8F0' : undefined,
                borderRadius: 3,
                boxShadow: isLight ? '0 10px 25px rgba(15,23,42,0.1)' : undefined,
              }
            }}
          >
            <DialogTitle sx={{ color: isLight ? '#0F172A' : undefined, fontWeight: 700 }}>Edit Organization {editOrg.update_request == "Requested" && (
              <Chip
                label="Update Request"
                size="small"
                variant="outlined"
                color="warning"
              />

            )}</DialogTitle>
            <DialogContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isLight ? '#0F172A' : undefined }}>Organization Name *</Typography>
                    <HelpTooltip
                      title="Organization Name"
                      purpose="Official legal or operating title of the church, ministry, or non-profit."
                      howItWorks="Identifies the tenant throughout the platform, mobile app, and admin reports."
                      mobileEffect="Displayed on user registration screens, community directories, and banner headers."
                    />
                  </Box>
                  <TextField
                    placeholder="Organization name"
                    value={editOrg.org_name || ""}
                    onChange={(e) => setEditOrg({ ...editOrg, org_name: e.target.value })}
                    fullWidth
                  />
                  {editOrg.update_request == "Requested" && (
                    <Typography variant="subtitle1" color="primary">
                      <b>New Organization Name:</b> {editOrg.edit_org_table[0].org_name}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isLight ? '#0F172A' : undefined }}>Contact Name *</Typography>
                    <HelpTooltip
                      title="Primary Contact Person"
                      purpose="Designated administrative point-of-contact for this organization."
                      howItWorks="Used for official communications, verification, and critical billing/system alerts."
                      mobileEffect="Available in the internal directory for escalated organizational inquiries."
                    />
                  </Box>
                  <TextField
                    placeholder="Contact person name"
                    value={editOrg.contact_person_name || ""}
                    onChange={(e) => setEditOrg({ ...editOrg, contact_person_name: e.target.value })}
                    fullWidth
                  />
                  {editOrg.update_request == "Requested" && (
                    <Typography variant="subtitle1" color="primary">
                      <b>New Contact Name:</b> {editOrg.edit_org_table[0].contact_person_name}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isLight ? '#0F172A' : undefined }}>Contact Email *</Typography>
                    <HelpTooltip
                      title="Organization Contact Email"
                      purpose="Primary administrative email inbox for notifications and password recovery."
                      howItWorks="Receives user request notifications and organization verification status updates."
                      mobileEffect="Rendered in support and contact details for church members."
                    />
                  </Box>
                  <TextField
                    placeholder="Contact email"
                    value={editOrg.email || ""}
                    onChange={(e) => setEditOrg({ ...editOrg, email: e.target.value })}
                    fullWidth
                  />
                  {editOrg.update_request == "Requested" && (
                    <Typography variant="subtitle1" color="primary">
                      <b>Contact Email:</b> {editOrg.edit_org_table[0].email}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isLight ? '#0F172A' : undefined }}>Phone Number</Typography>
                    <HelpTooltip
                      title="Contact Phone"
                      purpose="Direct helpline or office telephone number for the organization."
                      howItWorks="Used by admins and support personnel to verify emergency and pastoral requests."
                      mobileEffect="Allows mobile users to initiate one-tap direct calls to the ministry office."
                    />
                  </Box>
                  <TextField
                    placeholder="Phone number"
                    value={editOrg.phone || ""}
                    onChange={(e) => setEditOrg({ ...editOrg, phone: e.target.value })}
                    fullWidth
                  />
                  {editOrg.update_request == "Requested" && (
                    <Typography variant="subtitle1" color="primary">
                      <b>New Phone:</b> {editOrg.edit_org_table[0].phone}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isLight ? '#0F172A' : undefined }}>Address</Typography>
                    <HelpTooltip
                      title="Physical Headquarters / Campus"
                      purpose="Physical address of the worship facility or ministry headquarters."
                      howItWorks="Geolocates events, prayer gatherings, and community generosity distribution points."
                      mobileEffect="Powers in-app map routing, ride-connect pickup points, and distance calculations."
                    />
                  </Box>
                  <TextField
                    placeholder="Address"
                    value={editOrg.address || ""}
                    onChange={(e) => setEditOrg({ ...editOrg, address: e.target.value })}
                    fullWidth
                  />
                  {editOrg.update_request == "Requested" && (
                    <Typography variant="subtitle1" color="primary">
                      <b>New Address:</b> {editOrg.edit_org_table[0].address}
                    </Typography>
                  )}
                </Box>

                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isLight ? '#0F172A' : undefined }}>Website URL</Typography>
                    <HelpTooltip
                      title="Official Website"
                      purpose="External web portal or online church stream URL."
                      howItWorks="Must start with https:// for secure external browser launches."
                      mobileEffect="Provides a verified external link within the mobile organization profile."
                    />
                  </Box>
                  <TextField
                    placeholder="https://..."
                    value={editOrg.website || ""}
                    onChange={(e) => setEditOrg({ ...editOrg, website: e.target.value })}
                    fullWidth
                  />
                  {editOrg.update_request == "Requested" && (
                    <Typography variant="subtitle1" color="primary">
                      <b>New Website:</b> {editOrg.edit_org_table[0].website}
                    </Typography>
                  )}
                </Box>

                {/* Logo upload (optional, you can keep as text if you want) */}
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: isLight ? '#0F172A' : undefined }}>Organization Logo</Typography>
                    <HelpTooltip
                      title="Organization Brand Logo"
                      purpose="High-resolution branding mark representing the ministry or organization."
                      howItWorks="PNG, JPG, or SVG recommended with square 1:1 aspect ratio."
                      mobileEffect="Displayed as the organization icon across the app header, prayer closets, and events."
                    />
                  </Box>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={e => {
                      const file = e.target.files[0];
                      if (file) {
                        setEditOrg({ ...editOrg, logo: file });
                      }
                    }}
                  />
                  {/* Show current logo if exists */}
                  {editOrg.logo && typeof editOrg.logo === "string" && (
                    <Box sx={{ mt: 1 }}>
                      <img src={editOrg.logo} alt="Logo" style={{ maxWidth: 80, maxHeight: 80, borderRadius: 4 }} />
                    </Box>
                  )}

                  {editOrg.update_request === "Requested" &&
                    editOrg.edit_org_table[0]?.logo &&
                    typeof editOrg.edit_org_table[0]?.logo === "string" && (
                      <Typography variant="subtitle1" color="primary">
                        <b>New Logo:</b>  <img src={editOrg.edit_org_table[0]?.logo} alt="Logo" style={{ maxWidth: 80, maxHeight: 80, borderRadius: 4 }} />
                      </Typography>
                    )}

                </Box>


                {/* Important Links Section */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, color: isLight ? '#0F172A' : undefined }}>
                    <LinkIcon sx={{ color: isLight ? '#0D9488' : undefined }} /> Important Links
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* User Registration Link */}
                    <Paper
                      elevation={isLight ? 0 : 2}
                      sx={{
                        p: 2,
                        background: isLight ? '#F0FDFA' : 'linear-gradient(135deg, rgba(76, 161, 175, 0.1) 0%, rgba(44, 62, 80, 0.1) 100%)',
                        border: isLight ? '1px solid #CCFBF1' : '1px solid rgba(76, 161, 175, 0.3)',
                        borderRadius: 2
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                          <PersonAddIcon sx={{ color: isLight ? '#0D9488' : '#4CA1AF', fontSize: 28 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: isLight ? '#0F172A' : undefined }}>
                              User Registration Link
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: isLight ? '#475569' : 'text.secondary',
                                wordBreak: 'break-all',
                                fontSize: '0.875rem'
                              }}
                            >
                              {`${window.location.origin}/appuserregistration/${editOrg.short_code}`}
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          onClick={() => handleCopyLink(`${window.location.origin}/appuserregistration/${editOrg.short_code}`, 'Registration Link')}
                          sx={{
                            color: copiedLink === 'Registration Link' ? '#10B981' : (isLight ? '#0D9488' : '#4CA1AF'),
                            '&:hover': { background: isLight ? 'rgba(13, 148, 136, 0.1)' : 'rgba(76, 161, 175, 0.1)' }
                          }}
                          title="Copy Link"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>

                    {/* Android App Link */}
                    <Paper
                      elevation={isLight ? 0 : 2}
                      sx={{
                        p: 2,
                        background: isLight ? '#F0FDF4' : 'linear-gradient(135deg, rgba(61, 220, 132, 0.1) 0%, rgba(34, 197, 94, 0.1) 100%)',
                        border: isLight ? '1px solid #BBF7D0' : '1px solid rgba(34, 197, 94, 0.3)',
                        borderRadius: 2
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                          <AndroidIcon sx={{ color: '#16A34A', fontSize: 28 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: isLight ? '#0F172A' : undefined }}>
                              Android App Link
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: isLight ? '#475569' : 'text.secondary',
                                wordBreak: 'break-all',
                                fontSize: '0.875rem'
                              }}
                            >
                              https://play.google.com/store/apps/details?id=com.glocalprayer.ep&pcampaignid=web_share
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          onClick={() => handleCopyLink('https://play.google.com/store/apps/details?id=com.glocalprayer.ep&pcampaignid=web_share', 'Android Link')}
                          sx={{
                            color: copiedLink === 'Android Link' ? '#10B981' : '#16A34A',
                            '&:hover': { background: isLight ? 'rgba(22, 163, 74, 0.1)' : 'rgba(61, 220, 132, 0.1)' }
                          }}
                          title="Copy Link"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>

                    {/* iOS App Link */}
                    <Paper
                      elevation={isLight ? 0 : 2}
                      sx={{
                        p: 2,
                        background: isLight ? '#EFF6FF' : 'linear-gradient(135deg, rgba(147, 197, 253, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
                        border: isLight ? '1px solid #BFDBFE' : '1px solid rgba(59, 130, 246, 0.3)',
                        borderRadius: 2
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                          <AppleIcon sx={{ color: '#2563EB', fontSize: 28 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: isLight ? '#0F172A' : undefined }}>
                              iOS App Link
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: isLight ? '#475569' : 'text.secondary',
                                wordBreak: 'break-all',
                                fontSize: '0.875rem'
                              }}
                            >
                              https://apps.apple.com/in/app/glocal-prayer-network/id6479648726
                            </Typography>
                          </Box>
                        </Box>
                        <IconButton
                          onClick={() => handleCopyLink('https://apps.apple.com/in/app/glocal-prayer-network/id6479648726', 'iOS Link')}
                          sx={{
                            color: copiedLink === 'iOS Link' ? '#10B981' : '#2563EB',
                            '&:hover': { background: isLight ? 'rgba(37, 99, 235, 0.1)' : 'rgba(59, 130, 246, 0.1)' }
                          }}
                          title="Copy Link"
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  </Box>
                </Box>




              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
              <Button
                onClick={() => handleSaveOrgDraft(true)}
                startIcon={<DraftsIcon />}
                sx={{ color: isLight ? "#0D9488" : "#64d2e2", textTransform: "none", fontWeight: 600 }}
              >
                Save Draft
              </Button>
              <Box display="flex" gap={1}>
                <Button onClick={() => setEditOrg(null)}>Cancel</Button>
                {editOrg.update_request == "Requested" ? (
                  <>
                    <Button onClick={() => updateEditStatus(editOrg.id, "1")} variant="contained">Accept</Button>
                    <Button onClick={() => updateEditStatus(editOrg.id, "2")} variant="warning">Reject</Button>
                  </>
                ) : (
                  <Button onClick={saveEdit} variant="contained">Save</Button>
                )}
              </Box>
            </DialogActions>
          </Dialog>
        )}





        {/* View Dialog for Bulk Actions */}

        <Dialog
          open={openViewDialog}
          onClose={handleDialogClose}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              background: isLight ? '#FFFFFF' : undefined,
              border: isLight ? '1px solid #E2E8F0' : undefined,
              borderRadius: 3,
            }
          }}
        >
          <DialogTitle sx={{ color: isLight ? '#0F172A' : undefined, fontWeight: 700 }}>
            {
              Bulkstatus === 1
                ? "Activate List"
                : "Deactivate List"}
          </DialogTitle>
          <DialogContent>
            {selectedUsers.length === 0 && (
              <Typography variant="body2" sx={{ color: isLight ? '#64748B' : 'text.secondary' }}>
                No User selected.
              </Typography>
            )}
            {selectedUsers.map((user, index) => (
              <Typography key={user.id} variant="h6" sx={{ color: isLight ? '#0F172A' : undefined }}>
                {index + 1}. {user.name}
              </Typography>
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button onClick={BulkUpdateToUser} variant="contained" color="primary">
              Submit
            </Button>
          </DialogActions>
        </Dialog>

        {/* Organization Detail Modal */}
        {orgDetailModalOpen && selectedOrgForView && (
          <Dialog
            open={orgDetailModalOpen}
            onClose={() => {
              setOrgDetailModalOpen(false);
              setSelectedOrgForView(null);
            }}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "16px",
                bgcolor: isLight ? "#FFFFFF" : "#1e293b",
                color: isLight ? "#0F172A" : "#FFFFFF",
                boxShadow: isLight ? "0 20px 40px rgba(0,0,0,0.15)" : "0 20px 40px rgba(0,0,0,0.5)",
              }
            }}
          >
            <DialogTitle
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.1)",
                pb: 1.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <BusinessIcon sx={{ color: isLight ? "#0D9488" : "#4CA1AF" }} />
                <Typography variant="h6" fontWeight={700}>
                  Organization Details
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  setOrgDetailModalOpen(false);
                  setSelectedOrgForView(null);
                }}
                sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.7)" }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                {/* Header Profile */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Avatar
                    src={selectedOrgForView.logo || undefined}
                    alt={selectedOrgForView.org_name}
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "14px",
                      bgcolor: isLight ? "#F0FDFA" : "rgba(13, 148, 136, 0.2)",
                      color: isLight ? "#0D9488" : "#64d2e2",
                      border: isLight ? "2px solid #CCFBF1" : "2px solid rgba(76, 161, 175, 0.3)",
                      fontWeight: 700,
                      fontSize: "1.5rem",
                    }}
                    imgProps={{
                      onError: (e) => { e.target.onerror = null; e.target.src = ""; }
                    }}
                  >
                    {selectedOrgForView.org_name ? selectedOrgForView.org_name[0].toUpperCase() : <BusinessIcon sx={{ fontSize: 28 }} />}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={700}>
                      {selectedOrgForView.org_name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                      <Chip
                        label={selectedOrgForView.status}
                        color={statusColors[selectedOrgForView.status] || "default"}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                      {selectedOrgForView.update_request === "Requested" && (
                        <Chip
                          label="Update Requested"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                      <Chip
                        label={`${selectedOrgForView.total_users || 0} members`}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 600, color: isLight ? "#0D9488" : "#64d2e2" }}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Primary Info Box */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: "12px",
                    bgcolor: isLight ? "#F8FAFC" : "rgba(255,255,255,0.03)",
                    borderColor: isLight ? "#E2E8F0" : "rgba(255,255,255,0.08)",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)" }}>
                        Contact Person
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedOrgForView.contact || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)" }}>
                        Email
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedOrgForView.email || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)" }}>
                        Phone
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {selectedOrgForView.phone || "N/A"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)" }}>
                        Joined Date
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatDate(selectedOrgForView.created_at)} ({daysAgo(selectedOrgForView.created_at)})
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 3 }}>
                <Button
                  onClick={() => {
                    setOrgDetailModalOpen(false);
                    setSelectedOrgForView(null);
                  }}
                  sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.7)", textTransform: "none" }}
                >
                  Close
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SwapHorizIcon />}
                  onClick={() => {
                    const orgToSwitch = selectedOrgForView;
                    setOrgDetailModalOpen(false);
                    setSelectedOrgForView(null);
                    handleSwitchToOrganization(orgToSwitch.id, orgToSwitch.org_name);
                  }}
                  sx={{
                    borderColor: isLight ? "#0D9488" : "#4CA1AF",
                    color: isLight ? "#0D9488" : "#64d2e2",
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Switch Context
                </Button>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => {
                    const orgToEdit = selectedOrgForView;
                    setOrgDetailModalOpen(false);
                    setSelectedOrgForView(null);
                    handleEditOrganization(orgToEdit);
                  }}
                  sx={{
                    background: isLight ? "#0D9488" : "#177373",
                    color: "#FFFFFF",
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": { background: isLight ? "#0F766E" : "#135d5d" }
                  }}
                >
                  Edit Organization
                </Button>
              </Box>
            </DialogContent>
          </Dialog>
        )}

      </Box>
      <Toast toast={toast} setToast={setToast} />
      {/* Floating Settings Icon */}
      <Tooltip title="Organizations Settings" placement="left">
        <IconButton
          onClick={() => setSettingsOpen(true)}
          sx={{
            position: 'fixed', bottom: 32, right: 32,
            bgcolor: isLight ? '#FFFFFF' : 'rgba(76, 161, 175, 0.15)',
            border: isLight ? '1px solid #E2E8F0' : '1px solid rgba(76, 161, 175, 0.4)',
            color: isLight ? '#0D9488' : '#64d2e2',
            boxShadow: isLight ? '0 4px 14px rgba(15,23,42,0.1)' : undefined,
            width: 48, height: 48, zIndex: 1300, backdropFilter: 'blur(8px)',
            '&:hover': { bgcolor: isLight ? '#F0FDFA' : 'rgba(76, 161, 175, 0.3)', transform: 'rotate(20deg)' },
            transition: 'all 0.25s ease',
          }}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      <ModuleSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} moduleKey="organizations" moduleTitle="Organizations" />
      <DraftsModal
        open={draftsOpen}
        onClose={() => setDraftsOpen(false)}
        drafts={draftsList}
        onResumeDraft={handleResumeOrgDraft}
        onDeleteDraft={handleDeleteOrgDraft}
      />
    </>
  );
};

export default Organizations;